// src/Services/foreignCurrency.service.js

import config from "../config"
import selectedEnvironment from "../env"
import { ForeignCurrencyCode } from "../assests/Constants/constants"

const API_URL = `${config[selectedEnvironment].API_URL}/ForeignCurrencies`

// reverse lookup: 0 → "USD", 1 → "EUR", etc.
const codeNumberToString = Object.fromEntries(
  Object.entries(ForeignCurrencyCode).map(([k, v]) => [v, k])
)

/**
 * Fetch the ILS rate for one unit of the given currency on a date.
 *
 * @param {number|string}  fxCode    – either the numeric enum value or the string code
 * @param {Date|string}    date      – JS Date or ISO string
 * @param {string}         authToken – Bearer token
 * @param {AbortSignal}    [signal]
 * @returns {Promise<number>}        – the numeric ILS rate
 */
async function GetIlsRateForDate(fxCode, date, authToken, signal) {
  if (fxCode == null) {
    throw new Error("foreignCurrencyCode is required")
  }
  if (!date) {
    throw new Error("date is required")
  }

  // map 0 → "USD" etc
  const code = typeof fxCode === "number"
    ? codeNumberToString[fxCode]
    : fxCode

  if (!code) {
    throw new Error(`Unknown currency code: ${fxCode}`)
  }

  // format yyyy-MM-dd using local date so timezone does not shift the calendar day (e.g. 2026-03-09T00:00:00 stays 2026-03-09)
  const d = new Date(date)
  const year = d.getFullYear()
  const month = String(d.getMonth() + 1).padStart(2, '0')
  const dayNum = String(d.getDate()).padStart(2, '0')
  const day = `${year}-${month}-${dayNum}`
  const url = `${API_URL}/exchange-rate?code=${code}&date=${day}`


  const response = await fetch(url, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${authToken}`
    },
    signal
  })

  if (!response.ok) {
    throw new Error(`Currency API error ${response.status}`)
  }

  const json = await response.json()
  const { rate } = json
  
  
  if (rate == null) {
    throw new Error("Currency API did not return a rate")
  }

  return rate
}

function toLocalDateKey(date) {
  const d = new Date(date)
  const year = d.getFullYear()
  const month = String(d.getMonth() + 1).padStart(2, '0')
  const dayNum = String(d.getDate()).padStart(2, '0')
  return `${year}-${month}-${dayNum}`
}

function addDays(date, delta) {
  const d = new Date(date)
  d.setDate(d.getDate() + delta)
  return d
}

const FALLBACK_LOOKBACK_DAYS = 30

/**
 * Fetch the ILS rate, trying the document date first then alternate dates that return data.
 *
 * @returns {Promise<{ rate: number, usedDate: string }>}
 */
async function GetIlsRateForDateWithFallback(fxCode, documentDate, authToken, signal) {
  if (!documentDate) {
    throw new Error("date is required")
  }

  const documentDateKey = toLocalDateKey(documentDate)
  const tried = new Set()

  const tryDate = async (date) => {
    const key = toLocalDateKey(date)
    if (tried.has(key)) return null
    tried.add(key)
    try {
      const rate = await GetIlsRateForDate(fxCode, date, authToken, signal)
      if (rate != null && rate > 0) {
        return { rate, usedDate: key }
      }
    } catch {
      // try next candidate date
    }
    return null
  }

  const primary = await tryDate(documentDate)
  if (primary) return primary

  const today = new Date()
  const todayResult = await tryDate(today)
  if (todayResult) return todayResult

  for (let i = 1; i <= FALLBACK_LOOKBACK_DAYS; i += 1) {
    const candidate = addDays(documentDate, -i)
    const result = await tryDate(candidate)
    if (result) return result
  }

  throw new Error(`No exchange rate found for ${fxCode} near ${documentDateKey}`)
}


const ForeignCurrencyService = {
  GetIlsRateForDate,
  GetIlsRateForDateWithFallback,
}

export default ForeignCurrencyService
